import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Navigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Dropdown, Modal, Button } from 'react-bootstrap';
import { FaFilePdf } from 'react-icons/fa';
import { FiArrowLeft, FiMoreVertical, FiStar } from 'react-icons/fi';
import InternalFooter from '../components/InternalFooter/InternalFooter';
import { formatDateTimeToDDMMMYYYY } from '../../utils/dateFormatter';
import {
  getForum,
  getForumComments,
  changeForumStatus,
  toggleForumFavorite,
  deleteForum,
  exportForumPdf,
} from '../../services/ForumService';
import ForumCommentSection from './ForumCommentSection';
import { getForumPermissions } from './forumPermissions';
import { getForumParticipantDisplayList } from './forumParticipants';
import '../tasks/taskdetails.css';
import '../Documents/commonstyle.css';
import './forum.css';

const STATUS_ACTIONS = [
  { value: 'ongoing', label: 'Ongoing' },
  { value: 'on_hold', label: 'On Hold' },
  { value: 'closed', label: 'Closed' },
];

const statusLabel = (s) => (s || '').replace('_', ' ');

const ForumDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [forum, setForum] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [comments, setComments] = useState([]);
  const [relatedTasks, setRelatedTasks] = useState([]);
  const [isFavorite, setIsFavorite] = useState(false);
  const [loading, setLoading] = useState(true);
  const [infoOpen, setInfoOpen] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      const [forumRes, commentsRes] = await Promise.all([
        getForum(id),
        getForumComments(id),
      ]);
      if (forumRes.data?.success === false) {
        toast.error(forumRes.data.message || 'Failed to load forum');
        setForum(null);
        return;
      }
      if (commentsRes.data?.success === false) {
        toast.error(commentsRes.data.message || 'Failed to load comments');
      }
      setForum(forumRes.data.forum);
      setAttachments(forumRes.data.attachments || []);
      setRelatedTasks(forumRes.data.related_tasks || []);
      setIsFavorite(forumRes.data.is_favorite);
      setComments(commentsRes.data?.comments || []);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load forum');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [id]);

  const handleStatusChange = async (status) => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const { canSetClosed, canChangeStatus } = getForumPermissions(forum, user);
    if (!canChangeStatus) {
      toast.error('You are not allowed to change the status');
      return;
    }
    if (status === 'closed' && !canSetClosed) {
      toast.error('You are not allowed to close this discussion');
      return;
    }
    try {
      const res = await changeForumStatus(id, status);
      if (res.data?.success === false) {
        toast.error(res.data.message || 'Failed to update status');
        return;
      }
      toast.success('Status updated');
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update status');
    }
  };

  const handleFavorite = async () => {
    try {
      const res = await toggleForumFavorite(id);
      if (res.data?.success === false) {
        toast.error(res.data.message || 'Failed to update favorite');
        return;
      }
      setIsFavorite(res.data.is_favorite);
      toast.success(res.data.is_favorite ? 'Added to favorites' : 'Removed from favorites');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update favorite');
    }
  };

  const handleDeleteWithConfirmation = () => {
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    setShowDeleteModal(false);
    try {
      const res = await deleteForum(id);
      if (res.data?.success === false) {
        toast.error(res.data.message || 'Delete failed');
        return;
      }
      toast.success('Discussion deleted');
      navigate('/forum/list');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Delete failed');
    }
  };

  const handleCreateTask = (comment) => {
    if (!comment?.id) {
      toast.error('Comment is missing an id. Refresh the page.');
      return;
    }
    window.open(`/task/addtask?forumCommentId=${comment.id}`, '_blank');
    toast.info('Task form opened in new tab with prefilled data');
  };

  const handleExportPdf = async () => {
    try {
      const res = await exportForumPdf(id);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `forum_${id}.pdf`;
      link.click();
    } catch (err) {
      toast.error('PDF export failed');
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '60vh' }}>
        <div className="text-center">
          <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
            <span className="visually-hidden">Loading...</span>
          </div>
          <div className="mt-3">
            <h5>Loading discussion...</h5>
          </div>
        </div>
      </div>
    );
  }

  if (!forum) {
    return (
      <div>
        <p className="text-muted">Forum not found.</p>
        <button type="button" className="btn btn-link text-danger p-0" onClick={() => navigate('/forum/list')}>
          Back to list
        </button>
      </div>
    );
  }

  const userdata = JSON.parse(localStorage.getItem('user') || '{}');
  const { canEdit, canSetClosed, canDelete, canChangeStatus } = getForumPermissions(forum, userdata);
  const visibleStatusActions = STATUS_ACTIONS.filter(
    (s) => s.value !== 'closed' || canSetClosed
  );
  const currentStatusAction = STATUS_ACTIONS.find((s) => s.value === forum.status);
  const canUseStatusMenu = canChangeStatus && visibleStatusActions.length > 0 && forum.status !== 'closed';
  const participantEntries = getForumParticipantDisplayList(forum);

  return (
    <>
      <button
        type="button"
        className="back-button"
        onClick={() => navigate('/forum/list')}
        style={{
          display: 'flex',
          alignItems: 'center',
          background: 'none',
          border: 'none',
          color: '#C42126',
          fontSize: '1rem',
          cursor: 'pointer',
          marginBottom: '1rem',
        }}
      >
        <FiArrowLeft style={{ marginRight: '0.5rem' }} />
        Back
      </button>

      <div className="row forum-detail-row">
        <div className="col-xl-12 col-xxl-12">
          <div className="card heightcard forum-detail-card">
            <div className="card-header details-card-header forum-section-header forum-title-block">
              <div className="forum-title-block__info">
                <h5 className="text-base sm:text-lg font-semibold mb-0 forum-title-row__title">
                  <span className="forum-title-row__title-text">{forum.title}</span>
                  <button
                    type="button"
                    className={`forum-title-favorite-btn${isFavorite ? ' forum-title-favorite-btn--active' : ''}`}
                    onClick={handleFavorite}
                    aria-label={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
                    title={isFavorite ? 'Unfavorite' : 'Favorite'}
                  >
                    <FiStar aria-hidden />
                  </button>
                </h5>
                <div className="forum-title-block__labels">
                  <span className="forum-title-meta-label">
                    Start Date: {forum.start_date ? formatDateTimeToDDMMMYYYY(forum.start_date, false) : 'N/A'}
                  </span>
                  <span className="forum-title-meta-label">
                    Priority: {forum.priority || 'Medium'}
                  </span>
                </div>
              </div>
              <div className="forum-title-row__meta">
                <div className="forum-title-row__status">
                  {canUseStatusMenu ? (
                    <Dropdown align="end" className="forum-status-menu">
                      <Dropdown.Toggle
                        variant="link"
                        className="forum-title-row-btn forum-status-btn"
                        aria-label="Change discussion status"
                      >
                        <span className="forum-status-btn__label">
                          {currentStatusAction?.label || statusLabel(forum.status)}
                        </span>
                        <i className="fas fa-chevron-down forum-status-btn__chevron" aria-hidden></i>
                      </Dropdown.Toggle>
                      <Dropdown.Menu className="forum-status-menu__dropdown">
                        {visibleStatusActions
                          .filter((s) => s.value !== forum.status)
                          .map((s) => (
                            <Dropdown.Item
                              key={s.value}
                              onClick={() => handleStatusChange(s.value)}
                            >
                              {s.label}
                            </Dropdown.Item>
                          ))}
                      </Dropdown.Menu>
                    </Dropdown>
                  ) : (
                    <span className="forum-title-row-btn forum-status-btn forum-status-btn--readonly">
                      {currentStatusAction?.label || statusLabel(forum.status)}
                    </span>
                  )}
                </div>
                <button
                  type="button"
                  className={`forum-title-row-btn forum-info-tab${infoOpen ? ' forum-info-tab--active' : ''}`}
                  onClick={() => setInfoOpen((open) => !open)}
                  aria-expanded={infoOpen}
                  aria-controls="forum-info-panel"
                >
                  Info
                </button>
                <Dropdown align="end" className="forum-actions-menu">
                  <Dropdown.Toggle
                    variant="link"
                    className="forum-title-row-btn forum-title-row-btn--icon forum-actions-menu-toggle"
                    aria-label="Discussion actions"
                  >
                    <FiMoreVertical />
                  </Dropdown.Toggle>
                  <Dropdown.Menu>
                    <Dropdown.Item onClick={handleExportPdf}>
                      <FaFilePdf className="forum-actions-menu__icon forum-actions-menu__icon--pdf" aria-hidden />
                      Export PDF
                    </Dropdown.Item>
                    {canEdit && (
                      <Dropdown.Item onClick={() => navigate(`/forum/edit/${id}`)}>
                        <i className="fa fa-edit me-2" aria-hidden></i>
                        Edit
                      </Dropdown.Item>
                    )}
                    {canDelete && (
                      <>
                        <Dropdown.Divider />
                        <Dropdown.Item className="text-danger" onClick={handleDeleteWithConfirmation}>
                          <i className="fa fa-trash me-2" aria-hidden></i>
                          Delete
                        </Dropdown.Item>
                      </>
                    )}
                  </Dropdown.Menu>
                </Dropdown>
              </div>
            </div>
            <div className="card-body forum-detail-card__body">
              <div className="row">
                {forum.description && (
                  <div className="mb-4">
                    <div
                      className="forum-description-content"
                      dangerouslySetInnerHTML={{ __html: forum.description }}
                      style={{ minHeight: '60px' }}
                    />
                  </div>
                )}

                {attachments.length > 0 && (
                  <div className="col-lg-12 mb-2">
                    <label className="text-label">Attachments</label>
                    <div className="attachments-container p-3" style={{ background: '#f9f9f9', borderRadius: 6 }}>
                      <div className="d-flex flex-wrap gap-2">
                        {attachments.map((a) => (
                          <span key={a.id} className="badge bg-white text-dark border px-3 py-2">{a.file_name}</span>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {relatedTasks.length > 0 && (
                  <div className="col-lg-12 mb-3">
                    <label className="text-label">Related Tasks</label>
                    <div className="d-flex flex-wrap gap-2">
                      {relatedTasks.map((l) => (
                        <button
                          key={l.link_id}
                          type="button"
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => window.open(`/task/taskdetails/${l.task?.id}`, '_blank')}
                        >
                          <i className="fas fa-external-link-alt me-1"></i>
                          {l.task?.title}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="card heightcard">
            <div className="card-header details-card-header forum-section-header">
              <h5 className="text-base sm:text-lg font-semibold mb-0">Comments</h5>
            </div>
            <div className="card-body p-4">
              <ForumCommentSection
                forumId={id}
                comments={comments}
                onCommentAdded={load}
                onCreateTask={handleCreateTask}
                disabled={forum.status === 'closed'}
              />
            </div>
          </div>
        </div>
      </div>

      {infoOpen && (
        <button
          type="button"
          className="forum-info-backdrop"
          onClick={() => setInfoOpen(false)}
          aria-label="Close info panel"
        />
      )}

      <aside
        id="forum-info-panel"
        className={`forum-info-drawer${infoOpen ? ' forum-info-drawer--open' : ''}`}
        aria-hidden={!infoOpen}
      >
        <div className="forum-info-drawer-header">
          <h6 className="forum-info-drawer-title mb-0">Info</h6>
          <button
            type="button"
            className="forum-info-drawer-close"
            onClick={() => setInfoOpen(false)}
            aria-label="Close info panel"
          >
            <i className="fas fa-times" aria-hidden></i>
          </button>
        </div>
        <div className="forum-info-drawer-body">
          <div className="forum-info-field">
            <p className="forum-info-field-label">Created on</p>
            <p className="forum-info-field-value">
              {forum.created_at ? formatDateTimeToDDMMMYYYY(forum.created_at, true) : '—'}
            </p>
          </div>
          <div className="forum-info-field">
            <p className="forum-info-field-label">Creator</p>
            <p className="forum-info-field-value">{forum.creator?.full_name || '—'}</p>
          </div>
          <div className="forum-info-field">
            <p className="forum-info-field-label">Participants</p>
            {participantEntries.length > 0 ? (
              <div className="user-chip-list forum-info-participants">
                {participantEntries.map((entry) => (
                  <div className="user-chip" key={entry.id}>
                    <span className="user-chip-avatar">
                      {entry.type === 'org' ? (
                        <i className="fa fa-building" aria-hidden></i>
                      ) : (
                        entry.name[0]
                      )}
                    </span>
                    {entry.name}
                  </div>
                ))}
              </div>
            ) : (
              <p className="forum-info-field-value">—</p>
            )}
          </div>
          <div className="forum-info-field">
            <p className="forum-info-field-label">Email alerts</p>
            <p className="forum-info-field-value">{forum.email_notification ? 'Yes' : 'No'}</p>
          </div>
          <div className="forum-info-field">
            <p className="forum-info-field-label">Participants can edit</p>
            <p className="forum-info-field-value">{forum.allow_participants_edit ? 'Yes' : 'No'}</p>
          </div>
          <div className="forum-info-field">
            <p className="forum-info-field-label">Participants can close</p>
            <p className="forum-info-field-value">{forum.allow_participants_close ? 'Yes' : 'No'}</p>
          </div>
        </div>
      </aside>

      <Modal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Delete Discussion</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Are you sure you want to delete this discussion? Once deleted, you will not be able to recover this discussion!
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            className="doc-form-cancel-btn"
            onClick={() => setShowDeleteModal(false)}
          >
            Cancel
          </Button>
          <Button
            className="doc-form-submit-btn"
            onClick={handleDeleteConfirm}
          >
            Delete
          </Button>
        </Modal.Footer>
      </Modal>

      <InternalFooter />
    </>
  );
};

export default ForumDetail;

/** Legacy split-view URL → standard detail page */
export function ForumListDetailRedirect() {
  const { id } = useParams();
  return <Navigate to={`/forum/detail/${id}`} replace />;
}
